import {
	ForgeCDNEnvironment,
	type ProductEnvironment,
} from '@atlassian/forge-ui-types/environments';

import { getCDNEnvironment } from './getCDNEnvironment';
import { isFedRampInAnyContext } from './isFedRampInAnyContext';

/**
 * Returns the Forge CDN base domain for the given CDN environment.
 *
 * The returned value is the host suffix shared by all Forge CDN assets,
 * i.e. everything after the installation/app-specific subdomain:
 *
 *   cdn.<env>.atlassian-dev.net          (commercial)
 *   cdn.<env>.atlassian-dev-us-gov-mod.net  (FedRamp)
 *
 * The DEVELOPMENT environment is mapped to STAGING because a standalone
 * dev CDN does not exist.
 *
 * Safe to call during a server render: the perimeter comes from
 * {@link isFedRampInAnyContext}, which picks its source based on the context it is running in.
 *
 * Example — building a full domain:
 *   `${installationSlug}--${appSlug}.${getForgeCDNBaseDomain(cdnEnv)}`
 */
export function getForgeCDNBaseDomain(environment: ProductEnvironment): string {
	const fedRamp = isFedRampInAnyContext();
	const cdnEnv = getCDNEnvironment(environment);
	// DEVELOPMENT has no dedicated CDN; fall back to STAGING assets.
	const urlEnv = cdnEnv === ForgeCDNEnvironment.DEVELOPMENT ? ForgeCDNEnvironment.STAGING : cdnEnv;

	const secondLevelDomain = fedRamp ? 'atlassian-dev-us-gov-mod' : 'atlassian-dev';

	return `cdn.${urlEnv}.${secondLevelDomain}.net`;
}
